import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import Transition from '@ember/routing/-private/transition';
import CampaignsNewController, { createCampaign } from './controller';
import CampaignModel from 'cio/models/campaign';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import CustomFormsService from 'cio/services/custom-forms';
import TagsService from 'cio/services/tags';
import AnalyticsService from 'cio/services/analytics';
import { FormType } from 'cio/utils/forms';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomForm from 'cio/models/custom-form';
import { all } from 'rsvp';
import TagModel from 'cio/models/tag';
import RouterService from '@ember/routing/router-service';
import AuthService from 'cio/services/auth';

export default class NewRoute extends Route {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @service tags!: TagsService;
  @service router!: RouterService;
  @service auth!: AuthService;

  crumb() {
    return {
      name: 'New',
      path: 'campaigns.new',
    };
  }

  beforeModel(transition: Transition) {
    super.beforeModel(transition);
    if (Boolean(this.auth.hasPermission('journeys/campaigns/create')) === false) {
      this.router.transitionTo('campaigns.index');
      return;
    }
    return;
  }

  model() {
    return this.store.createRecord('campaign');
  }

  async fastForwardFormCampaign(model: CampaignModel, formID: string, formType: FormType) {
    await all([this.facebookLeadAds.initialize(), this.customForms.initialize()]);

    let tag = '';
    let form: CustomForm | FacebookLeadAdsForm | undefined;

    if (formType === FormType.FacebookLeadAds) {
      form = this.facebookLeadAds.forms.findBy('formId', formID);
      tag = 'Facebook Lead Ads';
    } else if (formType === FormType.Custom) {
      form = this.customForms.forms.findBy('formId', formID);
      tag = 'Custom form';
    } else if (formType === FormType.Typeform) {
      form = this.customForms.forms.findBy('formId', formID);
      tag = 'Typeform';
    } else if (formType === FormType.Jotform) {
      form = this.customForms.forms.findBy('formId', formID);
      tag = 'Jotform';
    }

    await this.tags.loadTags();

    model.set('name', form?.name);
    model.set('tags', [...(model.tags.content as TagModel[]), await this.tags.addTag(tag)]);

    return createCampaign(this, model);
  }

  afterModel(model: CampaignModel, transition: Transition) {
    let { triggerFormId, formType } = transition.to.queryParams;

    if (triggerFormId && formType) {
      return this.fastForwardFormCampaign(model, triggerFormId, formType as FormType).then(() =>
        this.router.transitionTo('campaigns.campaign.setup.trigger', model, {
          queryParams: { triggerFormId, formType },
        }),
      );
    }

    return super.afterModel(model, transition);
  }

  deactivate() {
    super.deactivate();

    this.rollbackModelFor();
  }

  async setupController(controller: CampaignsNewController, model: CampaignModel, transition: Transition) {
    super.setupController(controller, model, transition);
    controller.setDefaults();
  }

  resetController(controller: CampaignsNewController, isExiting: boolean, transition: Transition) {
    super.resetController(controller, isExiting, transition);
    controller.setDefaults();
  }
}
